<!-- Responsibility: Render the reusable NPC sheet dialog content surface.
Scope: NPC sheet dialog content only; modal lifecycle is owned by game-modal/app-shell. -->
<npc-sheet-dialog class="fm-npc-sheet" hidden={ !state.sheet }>
  <div if={ state.sheet } style="display: contents;">
  <header class="fm-header">
    <div class="fm-header-copy">
      <h2 if={ state.title }>{ state.title }</h2>
      <raw-html if={ state.desc } content={ state.desc } mode="prose" class="fm-desc"></raw-html>
      <raw-html if={ state.meta } content={ state.meta } mode="prose" class="fm-meta"></raw-html>
    </div>
    <button class="close-btn modal-close-hit" aria-label="Close npc dialog" onclick={ closeDialog }>X</button>
  </header>

    <div if={ state.resolvedImageUrl || state.imageLoading || state.fallbackLabel } class="fm-media-shell modal-media-slot float-media">
    <div if={ state.resolvedImageUrl } class="fm-image-wrap">
      <img class="fm-image" src={ state.resolvedImageUrl } alt="">
    </div>
    <div if={ state.imageLoading && !state.resolvedImageUrl } class="fm-image-placeholder">
      <span>···</span>
    </div>
    <div if={ !state.imageLoading && !state.resolvedImageUrl } class="fm-image-fallback">
      <span>{ state.fallbackLabel }</span>
    </div>
  </div>

  <div class="fm-npc-head">
    <span class="fm-npc-identity">{ state.sheet?.identity || 'Unknown' }</span>
    <span class="fm-npc-rel { state.sheet?.relationshipTone }">
      { state.sheet?.relationshipLabel } · REL { state.sheet?.relationship }
    </span>
  </div>

  <nav class="fm-tabs">
    <ul>
      <li each={ tab in state.tabs }>
        <button
          class={ state.tab === tab.id ? 'active' : '' }
          onclick={ () => setTab(tab.id) }
        >{ tab.label }</button>
      </li>
    </ul>
  </nav>

  <div if={ state.tab === 'profile' } class="fm-section fm-npc-section">
    <p class="fm-npc-lore">{ state.sheet?.lore || 'No lore recorded.' }</p>
    <div if={ state.sheet?.sex } class="fm-npc-line">SEX · { state.sheet?.sex.toUpperCase() }</div>
    <div if={ state.sheet?.intentions?.length }>
      <div class="fm-cards-label">INTENTIONS</div>
      <div class="fm-companion-tags">
        <span each={ intention in state.sheet?.intentions } class="fm-effect-chip active">{ intention }</span>
      </div>
      <div if={ (state.sheet?.overflow?.intentions ?? 0) > 0 } class="fm-overflow-note">
        +{ state.sheet?.overflow.intentions } more intentions hidden for stability.
      </div>
    </div>
    <div if={ state.sheet?.memories?.length }>
      <div class="fm-cards-label">MEMORIES</div>
      <ul class="memory-list">
        <li each={ mem in state.sheet?.memories }>{ mem }</li>
      </ul>
      <div if={ (state.sheet?.overflow?.memories ?? 0) > 0 } class="fm-overflow-note">
        +{ state.sheet?.overflow.memories } more memories hidden for stability.
      </div>
    </div>
  </div>

  <div if={ state.tab === 'inventory' } class="fm-section fm-npc-section">
    <div class="fm-cards-label">INVENTORY</div>
    <div class="fm-companion-tags" if={ state.sheet?.inventory?.length }>
      <button
        each={ itemName in state.sheet?.inventory }
        class="fm-chip fm-tag inventory actionable"
        data-tip={ inventoryInspectTip(itemName, 'NPC item') }
        data-tip-side="top"
        aria-label={ `Inspect npc item ${itemName}` }
        onclick={ () => this.props.content?.onOpenItem?.(itemName) }
      >
        <object-visual name={ itemName } size="sm" icon-only={true}></object-visual>
      </button>
    </div>
    <div if={ (state.sheet?.overflow?.inventory ?? 0) > 0 } class="fm-overflow-note">
      +{ state.sheet?.overflow.inventory } more inventory entries hidden.
    </div>
    <div if={ !state.sheet?.inventory?.length } class="fm-companion-empty">No known inventory.</div>
  </div>

  <div if={ state.tab === 'effects' } class="fm-section fm-npc-section">
    <div if={ state.sheet?.activeEffects?.length } class="fm-cards-label">ACTIVE EFFECTS</div>
    <div if={ state.sheet?.activeEffects?.length } class="fm-companion-tags">
      <span each={ effect in state.sheet?.activeEffects } class="fm-effect-chip active">{ this.props.content?.effectChipText?.(effect) }</span>
    </div>
    <div if={ (state.sheet?.overflow?.activeEffects ?? 0) > 0 } class="fm-overflow-note">
      +{ state.sheet?.overflow.activeEffects } more active effects hidden.
    </div>
    <div if={ state.sheet?.dormantEffects?.length } class="fm-cards-label">DORMANT EFFECTS</div>
    <div if={ state.sheet?.dormantEffects?.length } class="fm-companion-tags">
      <span each={ effect in state.sheet?.dormantEffects } class="fm-effect-chip dormant">{ this.props.content?.effectChipText?.(effect, true) }</span>
    </div>
    <div if={ (state.sheet?.overflow?.dormantEffects ?? 0) > 0 } class="fm-overflow-note">
      +{ state.sheet?.overflow.dormantEffects } more dormant effects hidden.
    </div>
    <div if={ !state.sheet?.activeEffects?.length && !state.sheet?.dormantEffects?.length } class="fm-companion-empty">No active or dormant effects.</div>
  </div>

  <footer if={ state.actions.length } class="modal-actions">
    <div class="fm-chips">
      <button
        each={ action in state.actions }
        class="fm-chip { action.danger ? 'danger' : '' }"
        onclick={ () => onAction(action) }
      >{ action.label }</button>
    </div>
  </footer>
  </div>

  <script>
    import { playEffect } from '../../../engine/audio.js'
    import { resolveDialogImage } from '../../dialog/dialog-media.ts'
    import { runUiAction } from '../../action-manager.ts'
    import ObjectVisual from '../object-visual.riot'
    import RawHtml from '../raw-html.riot'

    export default {
      components: { ObjectVisual, RawHtml },
      state: {
        tab: 'profile',
        tabs: [
          { id: 'profile', label: 'PROFILE' },
          { id: 'inventory', label: 'INVENTORY' },
          { id: 'effects', label: 'EFFECTS' }
        ],
        imageUrl: null,
        imageLoading: false,
        sheet: null,
        title: '',
        desc: '',
        meta: '',
        actions: [],
        resolvedImageUrl: null,
        fallbackLabel: '?',
      },

      onMounted() {
        this.openToken = Number(this.props.openToken || 0)
        this.syncState(this.props)
        this.update() // Trigger re-render after state sync
        this.syncImage()
      },

      onBeforeUpdate(props, state) {
        const token = Number(props.openToken || 0)
        if (token !== this.openToken) {
          this.openToken = token
          state.tab = 'profile'
        }
        this.syncState(props)
      },

      syncState(props) {
        const options = props.options ?? {}
        const sheet = props.content?.sheet ?? null
        const actions = Array.isArray(options.actions) ? options.actions : []
        const fallbackLabel = props.content?.fallbackLabel || '?'
        
        const nextState = {
          sheet,
          title: String(options.title ?? props.content?.title ?? '').trim(),
          desc: String(options.desc ?? props.content?.desc ?? '').trim(),
          meta: String(options.meta ?? props.content?.meta ?? '').trim(),
          actions,
          fallbackLabel,
          resolvedImageUrl: this.state.imageUrl || props.content?.imageUrl || null
        }
        
        Object.assign(this.state, nextState)
      },

      setTab(tab) {
        this.update({ tab: tab === 'inventory' || tab === 'effects' ? tab : 'profile' })
      },

      async syncImage() {
        const content = this.props.content ?? {}
        const directUrl = content.imageUrl ?? null
        const imageKind = content.imageKind
        const imageSeed = content.imageSeed
        const imageKey = directUrl ? `url:${directUrl}` : `${String(imageKind ?? '')}:${String(imageSeed ?? '')}`
        if (imageKey === this.imageKey) return
        this.imageKey = imageKey

        if (directUrl) {
          this.update({ imageUrl: directUrl, imageLoading: false })
          return
        }

        if (!imageKind || !imageSeed) {
          this.update({ imageUrl: null, imageLoading: false })
          return
        }

        this.update({ imageUrl: null, imageLoading: true })
        try {
          const imageUrl = await resolveDialogImage(imageKind, imageSeed)
          if (this.imageKey !== imageKey) return
          this.update({ imageUrl, imageLoading: false })
        } catch (error) {
          console.error('NPC sheet image load failed:', error)
          if (this.mediaKey !== imageKey) return
          this.update({ imageUrl: null, imageLoading: false })
        }
      },

      inventoryInspectTip(itemName, label) {
        return [
          `${label} · ${String(itemName ?? '').trim() || 'Unknown item'}`,
          'Tap to inspect full item details',
        ].join('\n')
      },

      onAction(action) {
        playEffect('ui_click')
        this.props.requestModalClose?.('action').then((closed) => {
          if (closed === false) return
          return runUiAction(action, {
            initiatorId: 'npc-sheet-dialog',
            targetName: this.state.title || undefined,
            targetType: 'npc',
          })
        }).catch((error) => console.error('NPC action failed:', error))
      },

      async closeDialog() {
        await this.props.requestModalClose?.('close-button')
      },
    }
  </script>

  <style>
    npc-sheet-dialog {
      display: block;
      overflow: auto;
      margin-top: 0.15rem;
    }

    .fm-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--space-md);
      margin-bottom: var(--space-sm);
    }

    .fm-header-copy {
      min-width: 0;
    }

    .fm-header h2 {
      margin: 0 0 var(--space-xs);
    }

    .float-media {
      float: left;
      width: 40% !important;
      margin-right: var(--space-md);
      margin-bottom: var(--space-xs);
      border-radius: 0.5rem;
      overflow: hidden;
      border: 1px solid var(--border);
    }

    .fm-tabs {
      margin-bottom: var(--space-sm);
      clear: both;
    }

    .fm-tabs ul {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-xs);
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .fm-tabs button {
      border: 1px solid var(--border);
      background: var(--bg);
      color: var(--text-muted);
      border-radius: 0.25rem;
      font-size: var(--type-3xs);
      letter-spacing: 0.08em;
      padding: var(--space-xs) var(--space-xs);
      cursor: pointer;
      font-family: inherit;
      transition: all 0.16s ease;

      &.active {
        color: var(--gold);
        border-color: var(--gold);
        background: color-mix(in srgb, var(--gold) var(--alpha-glow), #050812);
      }

      &:hover:not(.active) {
        border-color: var(--alpha-mid);
        color: var(--text-prose);
      }
    }

    .fm-companion-tags {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-xs);
    }

    .fm-tag {
      border: 1px solid var(--border);
      background: var(--bg);
      border-radius: 0.25rem;
      padding: var(--space-xs) var(--space-xs);
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      min-width: 0;
      max-width: 100%;

      &.actionable {
        appearance: none;
        cursor: pointer;
        text-align: left;
        font-family: inherit;
        transition: border-color 0.16s ease, background 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease;

        &:hover,
        &:focus-visible {
          border-color: color-mix(in srgb, var(--gold) var(--alpha-focus), transparent);
          background: color-mix(in srgb, var(--surface-card-2) var(--alpha-high), var(--bg));
          transform: translateY(-1px);
          box-shadow: var(--shadow-elev-2);
          outline: none;
        }
      }

      &.inventory {
        padding: var(--space-xs) var(--space-xs);
        justify-content: center;
      }
    }

    .fm-companion-empty {
      color: var(--text-dim);
      font-size: var(--space-sm);
    }

    .fm-overflow-note {
      color: color-mix(in srgb, var(--text-muted) var(--alpha-high), #dbe8ff);
      font-size: 0.68rem;
      letter-spacing: 0.03em;
    }

    .fm-effect-chip {
      border-radius: 999rem;
      padding: 0.18rem var(--space-xs);
      font-size: var(--type-3xs);
      letter-spacing: 0.05em;
      border: 1px solid var(--border);
      background: linear-gradient(160deg, var(--bg), var(--surface-card-3));
      color: var(--text-muted);
      font-weight: 700;
      line-height: 1.2;
      transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;

      &:hover {
        transform: translateY(-1px);
      }

      &.active {
        border-color: color-mix(in srgb, var(--gold) var(--alpha-mid), transparent);
        color: var(--gold);
        background:
          radial-gradient(circle at var(--alpha-glow) var(--alpha-low), color-mix(in srgb, var(--gold) var(--alpha-low), transparent), transparent 60%),
          linear-gradient(165deg, color-mix(in srgb, var(--gold) var(--alpha-low), #050812), #0b1322);
        box-shadow: 0 0 0 1px var(--gold-glow);
        animation: fx-active-pulse 2.4s ease-in-out infinite;
      }

      &.dormant {
        border-color: color-mix(in srgb, #64748b var(--alpha-mid), transparent);
        color: color-mix(in srgb, var(--text-muted) var(--alpha-ghost), #8ca0bc);
        background: var(--surface-card-3);
        opacity: 0.9;
      }
    }

    .fm-npc-head {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      gap: var(--space-xs);
      align-items: center;
      margin-bottom: var(--space-xs);
    }

    .fm-npc-identity {
      font-size: var(--space-sm);
      letter-spacing: 0.08em;
      color: var(--gold);
      font-weight: 800;
      text-transform: uppercase;
    }

    .fm-npc-rel {
      border-radius: 999rem;
      border: 1px solid var(--border);
      padding: 0.18rem var(--space-xs);
      font-size: var(--type-3xs);
      letter-spacing: 0.08em;
      color: var(--text-muted);
      background: var(--bg);

      &.friendly {
        border-color: var(--sem-success-border);
        color: var(--sem-success-fg);
        background: var(--sem-success-bg);
      }

      &.hostile {
        border-color: var(--sem-danger-border);
        color: var(--sem-danger-fg);
        background: var(--sem-danger-bg);
      }
    }

    .fm-section {
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    .fm-npc-lore {
      margin: 0;
      color: var(--text-prose);
      line-height: 1.5;
      font-size: 0.95rem;
    }

    .fm-npc-line {
      font-size: var(--type-3xs);
      letter-spacing: 0.08em;
      color: var(--text-muted);
      margin-top: var(--space-xs);
    }

    .fm-cards-label {
      color: var(--gold);
      letter-spacing: 0.1em;
      font-weight: 800;
      font-size: var(--type-3xs);
      margin-top: var(--space-sm);
      margin-bottom: var(--space-xs);
    }


    @media (max-width: 30rem) {
      .float-media {
        float: none;
        width: 100% !important;
        margin-right: 0;
      }
    }
  </style>
</npc-sheet-dialog>
